/**
 * React Query bindings for the assistant module. The two old list hooks
 * collapse into `useAgents` — the list endpoint returns phone numbers by
 * default — which also closes a latent bug: nothing ever invalidated the old
 * `["agents-with-phones"]` key, so the Agents page could go stale after a
 * create or delete.
 */

import { keepPreviousData, type Query, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PagedOptionsSource } from "@/hooks/use-paged-options";
import { useToast } from "@/hooks/use-toast";
import { isNotFound } from "@/lib/api-client";
import type { ListParams, OptionItem, PagedResult } from "@/lib/list-query";
import {
  Agent,
  AgentListItem,
  CreateAgentBody,
  UpdateAgentBody,
  createAssistant,
  deleteAssistant,
  getAssistant,
  listAssistants,
  updateAssistant,
} from "./assistant.service";
import { toolKeys } from "./tool.query";

export type { Agent, AgentConfig, AgentListItem } from "./assistant.service";
export type { ListParams, OptionItem, PagedResult };

export const agentKeys = {
  list: (workspaceId?: string, params?: ListParams) =>
    ["agents", workspaceId, params] as const,
  /** The pickers' pages of the same list — under `agents`, so the one
   * invalidation a mutation makes refreshes them too. */
  picker: (workspaceId?: string) => ["agents", workspaceId, "picker"] as const,
  detail: (agentId?: string) => ["agent", agentId] as const,
};

/**
 * A picker shows a name; the rest of the row is not its business. Typed on the
 * two fields it reads so it takes both a list row and a full agent.
 */
export const toAgentOption = (agent: { id: string; agent_name: string }): OptionItem => ({
  id: agent.id,
  name: agent.agent_name,
});

/**
 * Where every agent picker reads from — `AgentCombobox` and the Call History
 * Agent filter. It is `POST /assistant/list`, the same request the Agents table
 * pages through, one page at a time: no screen downloads every agent.
 */
export const agentPickerSource = (workspaceId?: string): PagedOptionsSource => ({
  queryKey: agentKeys.picker(workspaceId),
  fetchPage: async (params) => {
    const page = await listAssistants(workspaceId!, params);
    return { ...page, rows: page.rows.map(toAgentOption) };
  },
});

/**
 * One page of agents, with phone numbers. Rows are lean — see `AgentListItem`
 * before reaching for `config`.
 *
 * `placeholderData` keeps the previous page rendered while the next one loads,
 * so paging and typing in the search box do not blank the screen.
 */
export const useAgents = (workspaceId: string | undefined, params: ListParams) =>
  useQuery<PagedResult<AgentListItem>>({
    queryKey: agentKeys.list(workspaceId, params),
    queryFn: () => listAssistants(workspaceId!, params),
    enabled: !!workspaceId,
    placeholderData: keepPreviousData,
  });

const unlessNotFound = (query: Query) => !isNotFound(query.state.error);

/**
 * For a read keyed by one agent's id. Its 404 is final — the agent was deleted,
 * or the user cannot see it — so it is not retried (the page says so at once,
 * not after three tries), nor re-sent when the window regains focus or the
 * network returns: no request carries the dead id again.
 */
export const notFoundIsFinal = {
  retry: (failures: number, error: Error) => !isNotFound(error) && failures < 3,
  refetchOnWindowFocus: unlessNotFound,
  refetchOnReconnect: unlessNotFound,
};

/** One agent with its complete config. */
export const useAgent = (agentId?: string) =>
  useQuery<Agent>({
    queryKey: agentKeys.detail(agentId),
    queryFn: () => getAssistant(agentId!),
    enabled: !!agentId && agentId !== "new",
    ...notFoundIsFinal,
  });

/**
 * What still named a deleted agent. Its calls, campaigns, knowledge bases and
 * phone numbers keep their rows with the agent set to NULL (20260926120000 /
 * 20260926130000) — only its tools, Cal.com connection and chat sessions go
 * with it — so each list re-reads.
 */
const LISTS_NAMING_AN_AGENT = ["agents", "phone-numbers", "batch-campaigns", "call-logs", "kb-stores"];

export const useCreateAgent = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (agent: Omit<CreateAgentBody, "workspace_id">) =>
      createAssistant({ ...agent, workspace_id: workspaceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      toast({
        title: "Agent created",
        description: "Your new agent has been created successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Failed to create agent",
        description: error.message,
      });
    },
  });
};

export const useUpdateAgent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ agentId, updates }: { agentId: string; updates: UpdateAgentBody }) =>
      updateAssistant(agentId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(variables.agentId) });
      // The save rewrites the agent's native rows from the config, and the editor
      // seeds its native fields from those rows: a stale copy would put the old
      // values back on screen, and the next Publish would write them to the row.
      queryClient.invalidateQueries({ queryKey: toolKeys.all(variables.agentId) });
      toast({
        title: "Agent updated",
        description: "Your agent has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Failed to update agent",
        description: error.message,
      });
    },
  });
};

export const useDeleteAgent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (agentId: string) => deleteAssistant(agentId),
    onSuccess: (_, agentId) => {
      // Its own copy goes: Back to its page must say it was deleted, not show
      // the old config with Publish live.
      queryClient.removeQueries({ queryKey: agentKeys.detail(agentId) });
      for (const key of LISTS_NAMING_AN_AGENT) queryClient.invalidateQueries({ queryKey: [key] });
      toast({
        title: "Agent deleted",
        description: "Your agent has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Failed to delete agent",
        description: error.message,
      });
    },
  });
};
